/**
 * GlobalFilterDropdown - Dropdown menu for setting global account and environment filters
 *
 * Can be placed in the header to allow users to set persistent filters.
 */

import React, { useCallback, useState } from 'react'
import { Check, ExpandMore, FilterList } from '@mui/icons-material'

import { useGlobalFilter } from '../contexts/GlobalFilterContext'
import { useEscapeKey } from '../hooks/useEscapeKey'

type GlobalFilterDropdownProps = {
  className?: string
  disabled?: boolean
}

// @purpose Header dropdown with account/environment tabs for setting persistent global filters
const GlobalFilterDropdown = ({
  className = '',
  disabled = false,
}: GlobalFilterDropdownProps): React.JSX.Element => {
  const { filters, facets, loading, hasActiveFilters, updateFilter } =
    useGlobalFilter()
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'account' | 'environment'>(
    'account',
  )

  // PENG-13715: dismiss with Escape (backdrop-click already dismisses).
  // Filters aren't mutated on close, so no filter state changes here.
  const closeDropdown = useCallback(() => setIsOpen(false), [])
  useEscapeKey(isOpen, closeDropdown)

  const handleToggle = () => {
    if (disabled) return
    setIsOpen(!isOpen)
  }

  const handleAccountSelect = (accountId: string) => {
    const currentAccounts = filters.accountId
    const isSelected = currentAccounts.includes(accountId)

    if (isSelected) {
      updateFilter(
        'accountId',
        currentAccounts.filter(id => id !== accountId),
      )
    } else {
      updateFilter('accountId', [...currentAccounts, accountId])
    }
  }

  const handleEnvironmentSelect = (environment: string) => {
    const currentEnvironments = filters.environment
    const isSelected = currentEnvironments.includes(environment)

    if (isSelected) {
      updateFilter(
        'environment',
        currentEnvironments.filter(env => env !== environment),
      )
    } else {
      updateFilter('environment', [...currentEnvironments, environment])
    }
  }

  const handleSelectAllAccounts = () => {
    if (facets?.accountIds) {
      updateFilter('accountId', [...facets.accountIds])
    }
  }

  const handleClearAccounts = () => {
    updateFilter('accountId', [])
  }

  const handleSelectAllEnvironments = () => {
    if (facets?.environments) {
      updateFilter('environment', [...facets.environments])
    }
  }

  const handleClearEnvironments = () => {
    updateFilter('environment', [])
  }

  // Select-All / Clear are no-ops when nothing is selectable or everything is
  // already in the terminal state, so disable them there rather than letting
  // them look interactive. `facets?.accountIds`/`environments` are the total
  // set the corresponding Select-All action pushes into the filter, so we
  // check membership against those, not against `facets.accounts` (which may
  // be a superset when the richer shape carries has-resources flags — see the
  // account list below).
  //
  // `allXSelected` uses `.every(id => filter.includes(id))` rather than length
  // equality: the filter can carry ids that aren't in the current facet set
  // (stale URL-restored ids, ids left over from a tenant switch), so
  // `filter.length === facet.length` can be true even while some facet id is
  // still unselected — in which case Select-All would wrongly disable. The
  // guard on the non-empty available set stays, since `.every` returns true
  // for an empty array.
  const allAccountIds = facets?.accountIds ?? []
  const allEnvironments = facets?.environments ?? []
  const selectedAccountSet = new Set(filters.accountId)
  const selectedEnvironmentSet = new Set(filters.environment)
  const hasAccountSelection = filters.accountId.length > 0
  const hasEnvironmentSelection = filters.environment.length > 0
  const allAccountsSelected =
    allAccountIds.length > 0 &&
    allAccountIds.every(id => selectedAccountSet.has(id))
  const allEnvironmentsSelected =
    allEnvironments.length > 0 &&
    allEnvironments.every(env => selectedEnvironmentSet.has(env))

  // Text-link styling for the Select-All / Clear pair. Disabled state drops
  // the underline + colour and swaps the cursor; we don't rely on Tailwind's
  // `disabled:` variant here so we can override the `hover:underline` on the
  // enabled branch without specificity fights.
  const actionLinkClass = (isDisabled: boolean): string =>
    isDisabled
      ? 'text-xs text-gray-400 dark:text-gray-500 cursor-not-allowed'
      : 'text-xs text-blue-600 dark:text-blue-400 hover:underline'

  const getButtonClassName = () => {
    // On-dark treatment — this control lives only on the Deep Blue header bar.
    if (disabled) {
      return 'opacity-50 bg-header-pill border-header-line text-header-muted'
    }
    if (hasActiveFilters) {
      return 'bg-blue-500/20 border-blue-400 text-white'
    }
    return 'bg-header-pill border-header-line text-white hover:bg-header-line'
  }

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button - styled to match TenantSelector height */}
      <button
        onClick={handleToggle}
        className={`flex items-center gap-2 px-4 py-1 border rounded-lg transition-colors ${getButtonClassName()}`}
        aria-label="Global Filters"
        data-testid="global-filter-dropdown-trigger"
        disabled={disabled}
      >
        <FilterList sx={{ fontSize: 20 }} />
        <div className="text-left">
          <div className="font-medium text-sm">Filters</div>
          <div className="text-xs text-header-muted">
            {hasActiveFilters
              ? `${filters.accountId.length + filters.environment.length} ${disabled ? 'INACTIVE' : 'active'}`
              : 'All data'}
          </div>
        </div>
        <ExpandMore
          sx={{ fontSize: 16 }}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Content */}
          <div className="absolute top-full left-0 mt-2 w-100 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab('account')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'account'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Accounts
                {filters.accountId.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                    {filters.accountId.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('environment')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'environment'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Environments
                {filters.environment.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                    {filters.environment.length}
                  </span>
                )}
              </button>
            </div>

            {/* Content */}
            <div className="max-h-64 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  Loading...
                </div>
              ) : activeTab === 'account' ? (
                <>
                  {/* Account Actions */}
                  <div className="flex gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <button
                      onClick={handleSelectAllAccounts}
                      disabled={allAccountsSelected}
                      title={
                        allAccountsSelected
                          ? 'All accounts are already selected'
                          : undefined
                      }
                      className={actionLinkClass(allAccountsSelected)}
                    >
                      Select All
                    </button>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <button
                      onClick={handleClearAccounts}
                      disabled={!hasAccountSelection}
                      title={
                        !hasAccountSelection
                          ? 'No accounts selected to clear'
                          : undefined
                      }
                      className={actionLinkClass(!hasAccountSelection)}
                    >
                      Clear
                    </button>
                  </div>

                  {/* Account List — pre-sorted by the backend (resource
                      count descending, PENG-13403 PM follow-up); render in
                      the order given, never re-sort client-side. Falls back
                      to bare accountIds (no has-resources label) if the
                      richer accounts array is unavailable for any reason. */}
                  {facets?.accounts && facets.accounts.length > 0 ? (
                    facets.accounts.map(account => (
                      <button
                        key={account.id}
                        onClick={() => handleAccountSelect(account.id)}
                        className={`w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          filters.accountId.includes(account.id)
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : ''
                        }`}
                      >
                        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                          {account.id}
                          {!account.hasResources && (
                            <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 italic">
                              (no resources available)
                            </span>
                          )}
                        </span>
                        {filters.accountId.includes(account.id) && (
                          <Check
                            sx={{ fontSize: 16 }}
                            className="text-blue-600 dark:text-blue-400 flex-shrink-0"
                          />
                        )}
                      </button>
                    ))
                  ) : facets?.accountIds && facets.accountIds.length > 0 ? (
                    facets.accountIds.map(accountId => (
                      <button
                        key={accountId}
                        onClick={() => handleAccountSelect(accountId)}
                        className={`w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          filters.accountId.includes(accountId)
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : ''
                        }`}
                      >
                        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                          {accountId}
                        </span>
                        {filters.accountId.includes(accountId) && (
                          <Check
                            sx={{ fontSize: 16 }}
                            className="text-blue-600 dark:text-blue-400 flex-shrink-0"
                          />
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                      No accounts available
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Environment Actions */}
                  <div className="flex gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <button
                      onClick={handleSelectAllEnvironments}
                      disabled={allEnvironmentsSelected}
                      title={
                        allEnvironmentsSelected
                          ? 'All environments are already selected'
                          : undefined
                      }
                      className={actionLinkClass(allEnvironmentsSelected)}
                    >
                      Select All
                    </button>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <button
                      onClick={handleClearEnvironments}
                      disabled={!hasEnvironmentSelection}
                      title={
                        !hasEnvironmentSelection
                          ? 'No environments selected to clear'
                          : undefined
                      }
                      className={actionLinkClass(!hasEnvironmentSelection)}
                    >
                      Clear
                    </button>
                  </div>

                  {/* Environment List */}
                  {facets?.environments && facets.environments.length > 0 ? (
                    facets.environments.map(environment => (
                      <button
                        key={environment}
                        onClick={() => handleEnvironmentSelect(environment)}
                        className={`w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          filters.environment.includes(environment)
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : ''
                        }`}
                      >
                        <span className="text-sm text-gray-900 dark:text-gray-100 capitalize">
                          {environment}
                        </span>
                        {filters.environment.includes(environment) && (
                          <Check
                            sx={{ fontSize: 16 }}
                            className="text-blue-600 dark:text-blue-400 flex-shrink-0"
                          />
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                      No environments available
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default GlobalFilterDropdown
